import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { ScrollArea } from "../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { motion } from "framer-motion";
import { Search, Users, Star } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
};

export default function Clients() {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientInvoices, setClientInvoices] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    fetchClients();
  }, [token, activeOnly]);

  useEffect(() => {
    if (search) {
      setFilteredClients(clients.filter(c => 
        c.name.toLowerCase().includes(search.toLowerCase())
      ));
    } else {
      setFilteredClients(clients);
    }
  }, [search, clients]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/clients?active_only=${activeOnly}`, { headers });
      setClients(res.data);
      setFilteredClients(res.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientInvoices = async (clientName) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/clients/${encodeURIComponent(clientName)}/invoices`, { headers });
      setClientInvoices(res.data);
    } catch (error) {
      console.error("Failed to fetch client invoices:", error);
      toast.error("Erreur lors du chargement des factures");
    }
  };

  const handleClientClick = async (client) => {
    setSelectedClient(client);
    setSheetOpen(true);
    await fetchClientInvoices(client.name);
  };

  const toggleRecurring = async (clientName, isRecurring) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/clients/${encodeURIComponent(clientName)}/recurring?is_recurring=${!isRecurring}`, {}, { headers });
      toast.success(isRecurring ? "Client retiré des récurrents" : "Client marqué comme récurrent");
      fetchClients();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">
              Clients
            </h1>
            <p className="text-muted-foreground text-sm">
              {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="active-only"
              checked={activeOnly}
              onCheckedChange={setActiveOnly}
              data-testid="active-only-switch"
            />
            <Label htmlFor="active-only" className="text-sm text-muted-foreground">
              Actifs uniquement
            </Label>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 w-full sm:w-64"
            data-testid="client-search-input"
          />
        </div>
      </div>

      {/* Client List */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="h-36 skeleton rounded-lg"></div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
        >
          {filteredClients.map((client, index) => (
            <motion.div
              key={client.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.02 }}
            >
              <Card 
                className="bg-white border card-hover cursor-pointer"
                onClick={() => handleClientClick(client)}
                data-testid={`client-card-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate text-sm" title={client.name}>
                        {client.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${client.is_active ? 'status-active' : 'status-inactive'}`}
                        >
                          {client.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                        {client.is_recurring && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                            <Star className="w-3 h-3 mr-1" />
                            Récurrent
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">CA Total HT</p>
                      <p className="font-mono text-foreground font-medium">{formatCurrency(client.total_ht)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">CA {new Date().getFullYear()}</p>
                      <p className="font-mono text-foreground font-medium">{formatCurrency(client.year_ht)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Factures</p>
                      <p className="font-mono text-foreground">{client.invoice_count}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Dernière</p>
                      <p className="font-mono text-foreground text-xs">{formatDate(client.last_invoice_date)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Client Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl bg-white">
          <SheetHeader>
            <SheetTitle className="text-foreground flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              {selectedClient?.name}
            </SheetTitle>
          </SheetHeader>
          
          {clientInvoices && (
            <div className="mt-6 space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">CA Total HT</p>
                  <p className="text-xl font-mono text-foreground font-bold">{formatCurrency(clientInvoices.total_ht)}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Factures</p>
                  <p className="text-xl font-mono text-foreground font-bold">{clientInvoices.count}</p>
                </div>
              </div>

              {/* Recurring Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-foreground">Client récurrent</span>
                </div>
                <Switch
                  checked={selectedClient?.is_recurring}
                  onCheckedChange={() => toggleRecurring(selectedClient?.name, selectedClient?.is_recurring)}
                  data-testid="toggle-recurring"
                />
              </div>

              {/* Invoices Table */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3">Historique des factures</h4>
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs uppercase text-muted-foreground">N°</TableHead>
                        <TableHead className="text-xs uppercase text-muted-foreground">Type</TableHead>
                        <TableHead className="text-xs uppercase text-muted-foreground">Date</TableHead>
                        <TableHead className="text-xs uppercase text-muted-foreground text-right">HT</TableHead>
                        <TableHead className="text-xs uppercase text-muted-foreground text-right">TTC</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientInvoices.invoices.map((invoice) => (
                        <TableRow key={invoice.numero} className="table-row-hover">
                          <TableCell className="font-mono text-sm">{invoice.numero}</TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${invoice.type === 'Facture' ? 'status-facture' : 'status-avoir'}`}
                            >
                              {invoice.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{formatDate(invoice.date)}</TableCell>
                          <TableCell className="font-mono text-sm text-right">{formatCurrency(invoice.total_ht)}</TableCell>
                          <TableCell className="font-mono text-sm text-right">{formatCurrency(invoice.total_ttc)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
